import React, { useState, useEffect, useRef } from 'react';
import { DefaultButton, Modal, TextField, Stack, PrimaryButton, IconButton } from '@fluentui/react';
import { BlobServiceClient } from '@azure/storage-blob';
import { Buffer } from 'buffer'; // Import the Buffer polyfill
import { mergeStyles } from '@fluentui/react/lib/Styling';
import { uploadImageToMultipleSizes } from '../Util/imageUploader';
import env from '../../env';
const { REACT_APP_SAS_TOKEN } = env;

const modalStyles = mergeStyles({
    width: '800px',
    maxWidth: '100%',
    padding: '20px',
    backgroundColor: 'white',
    boxSizing: 'border-box',
});

const imageCardStyles = mergeStyles({
    width: '170px',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
    borderRadius: '5px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.3s, box-shadow 0.3s',
    ':hover': {
        transform: 'scale(1.05)',
        boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
    },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

const imageStyles = mergeStyles({
    width: 'auto',
    height: '100px',
    objectFit: 'cover',
});

const imageNameStyles = mergeStyles({
    padding: '10px',
    textAlign: 'center',
    fontSize: '14px',
    backgroundColor: '#f9f9f9',
    borderTop: '1px solid #ddd',
});

window.Buffer = Buffer;

const ImageGallery = ({ 
    setFieldValue, 
    fieldName, 
    blobUrl, 
    containerName, 
    folderName, 
    imageUrl, 
    folderArray,
    heading = 'Select an image from Blob Storage',
    btnText = 'Image Library',
    uploadBtnText = 'Upload Image',
    sizePathGroup = [],
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [images, setImages] = useState([]);
    const fileInputRef = useRef(null);
    
    useEffect(() => {
        if (isModalOpen) {
            fetchImagesFromBlob();
        }
    }, [isModalOpen]);

    const fetchImagesFromBlob = async () => {
        const blobServiceClient = new BlobServiceClient(`${blobUrl}${REACT_APP_SAS_TOKEN}`);

        const containerClient = blobServiceClient.getContainerClient(containerName);

        // Get images from a single or multiple folders.
        // single ex: folderName='IMAGE/icons/Prioritized-Financial-Plan/'
        // multiple ex: folderArray={['IMAGE/icons/Prioritized-Financial-Plan/', 'IMAGE/icons/Prioritized-Financial-Plan/Topics']}
        const folders = folderArray?.length > 0 ? folderArray : [folderName];

        const blobs = [];
        for (const folder of folders) {
            for await (const blob of containerClient.listBlobsFlat({ prefix: folder })) {
                blobs.push({
                    name: blob.name,
                    lastModified: blob.properties.lastModified,
                });
            }
        }
        blobs.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));

        setImages(blobs); 
    };

    const handleImageClick = (image) => {
        const imagePath = `${imageUrl}${image.name}`;
        setFieldValue(fieldName, imagePath); // Update Formik field value
        setIsModalOpen(false); 
    };

    const toggleModal = () => setIsModalOpen(!isModalOpen);

    const handleFileChange = async (event) => {
        const fileToUpload = event.target.files[0];
        if (!fileToUpload) return;

        const blobServiceClient = new BlobServiceClient(`${blobUrl}${REACT_APP_SAS_TOKEN}`);
        const containerClient = blobServiceClient.getContainerClient(containerName);

        const sizesToUse = sizePathGroup.length > 0
            ? sizePathGroup
            : [{ path: folderName.endsWith('/') ? folderName : folderName + '/', width: undefined, height: undefined }];

        try {
            await uploadImageToMultipleSizes({
                file: fileToUpload,
                sizes: sizesToUse,
                containerClient,
                contentType: fileToUpload.type,
            });
            fetchImagesFromBlob(); 
        } catch (error) {
            console.error("Upload failed:", error.message);
            alert('Upload failed, please try again.');
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current.click();
    };

    return (
        <div>
            <DefaultButton onClick={toggleModal} text={btnText} />
            <Modal
                isOpen={isModalOpen}
                onDismiss={toggleModal}
                isBlocking={false}
                styles={{ main: { maxWidth: '800px', width: '100%', overflow: 'hidden' } }}
            >
                <div className={modalStyles}>
                    <Stack tokens={{ childrenGap: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <h3>{heading}</h3>
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                            <PrimaryButton text={uploadBtnText} onClick={handleUploadClick} />
                        </div>
                        <Stack horizontal wrap tokens={{ childrenGap: 20 }}>

                            {images.map((image, index) => (
                                <div
                                    key={index}
                                    className={imageCardStyles}
                                    onClick={() => handleImageClick(image)}
                                >
                                    <img
                                        src={`${blobUrl}/${containerName}/${image.name}${REACT_APP_SAS_TOKEN}`}
                                        alt={image.name}
                                        className={imageStyles}
                                    />
                                    <div className={imageNameStyles}>{image.name.split('/').pop()}</div>
                                </div>
                            ))}

                        </Stack>
                    </Stack>
                </div>
            </Modal>
        </div>
    );
};

export default ImageGallery;
